// ABOUTME: Excel-like data grid for editing chart data with dynamic multi-series columns.
// ABOUTME: Supports category + N series columns, add/remove series, row operations, and validation display.

import { useState, useRef, useEffect, useCallback } from 'react';
import { DataGrid, renderTextEditor, type Column, type RenderCellProps } from 'react-data-grid';
import { Plus, X, AlertCircle } from 'lucide-react';
import type { ValidationError } from '@/utils/chartValidation';
import 'react-data-grid/lib/styles.css';

// CSS overrides for react-data-grid to match the app's design system.
// Uses the app's CSS custom properties which adapt to light/dark mode.
const gridStyleOverrides = {
  '--rdg-header-background-color': 'var(--color-surface-100)',
  '--rdg-header-draggable-background-color': 'var(--color-surface-200)',
  '--rdg-background-color': 'var(--color-surface-0)',
  '--rdg-border-color': 'var(--color-surface-300)',
  '--rdg-color': 'var(--color-surface-800)',
  '--rdg-row-hover-background-color': 'var(--color-surface-50)',
  '--rdg-row-selected-background-color': 'var(--color-primary-50, hsl(207deg 76% 92%))',
  '--rdg-row-selected-hover-background-color': 'var(--color-primary-100, hsl(207deg 76% 88%))',
  '--rdg-selection-color': 'var(--color-primary-600)',
} as React.CSSProperties;

export interface ChartDataEditorProps {
  data: Record<string, unknown>[];
  categoryKey: string;
  seriesKeys: string[];
  validationErrors: ValidationError[];
  onChange: (data: Record<string, unknown>[], categoryKey: string, seriesKeys: string[]) => void;
}

interface RowWithIndex extends Record<string, unknown> {
  _index: number;
}

// Inline editable header for renaming series keys
function SeriesHeaderEditor({
  seriesKey,
  seriesKeys,
  categoryKey,
  onRename,
  onRemove,
  canRemove,
}: {
  seriesKey: string;
  seriesKeys: string[];
  categoryKey: string;
  onRename: (oldKey: string, newKey: string) => void;
  onRemove: (key: string) => void;
  canRemove: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(seriesKey);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commitRename = useCallback(() => {
    if (committedRef.current) return;
    const trimmed = editValue.trim();

    // Validate: not empty, not unchanged, not a duplicate key, not the category key
    if (
      trimmed === '' ||
      trimmed === seriesKey ||
      (trimmed !== seriesKey && seriesKeys.includes(trimmed)) ||
      trimmed === categoryKey
    ) {
      setEditing(false);
      setEditValue(seriesKey);
      return;
    }

    committedRef.current = true;
    setEditing(false);
    onRename(seriesKey, trimmed);
  }, [editValue, seriesKey, seriesKeys, categoryKey, onRename]);

  const cancelRename = useCallback(() => {
    setEditing(false);
    setEditValue(seriesKey);
  }, [seriesKey]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitRename();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelRename();
      }
    },
    [commitRename, cancelRename]
  );

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="text"
          className="flex-1 min-w-0 px-1 py-0 text-sm border border-primary-300 rounded-sm outline-none bg-surface-0"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {canRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(seriesKey);
          }}
          className="p-0.5 rounded-sm cursor-pointer transition-colors flex-shrink-0 text-surface-400 hover:text-error hover:bg-error-light"
          title="Remove series"
        >
          <X className="w-3 h-3" />
        </button>
      )}
      <span
        className="truncate cursor-pointer"
        onDoubleClick={() => {
          committedRef.current = false;
          setEditValue(seriesKey);
          setEditing(true);
        }}
      >
        {seriesKey}
      </span>
    </div>
  );
}

export function ChartDataEditor({
  data,
  categoryKey,
  seriesKeys,
  validationErrors,
  onChange,
}: ChartDataEditorProps) {
  // Add index to rows for tracking
  const rowsWithIndex: RowWithIndex[] = data.map((row, index) => ({
    ...row,
    _index: index,
  }));

  const handleRowsChange = (rows: RowWithIndex[]) => {
    // Remove the _index field before passing back
    const cleanedRows: Record<string, unknown>[] = rows.map(({ _index, ...rest }) => rest);
    onChange(cleanedRows, categoryKey, seriesKeys);
  };

  const handleAddRow = () => {
    const newRow: Record<string, unknown> = { [categoryKey]: '' };
    for (const key of seriesKeys) {
      newRow[key] = 0;
    }
    onChange([...data, newRow], categoryKey, seriesKeys);
  };

  const handleDeleteRow = (index: number) => {
    const newData = data.filter((_, i) => i !== index);
    onChange(newData, categoryKey, seriesKeys);
  };

  const handleAddSeries = () => {
    const name = window.prompt('Series name:');
    if (!name || name.trim() === '') return;
    const trimmedName = name.trim();

    // Do not add a duplicate series key
    if (seriesKeys.includes(trimmedName) || trimmedName === categoryKey) return;

    const newSeriesKeys = [...seriesKeys, trimmedName];
    const newData = data.map((row) => ({ ...row, [trimmedName]: 0 }));
    onChange(newData, categoryKey, newSeriesKeys);
  };

  const handleRenameSeries = (oldKey: string, newKey: string) => {
    const newSeriesKeys = seriesKeys.map((k) => (k === oldKey ? newKey : k));
    const newData = data.map((row) => {
      const result: Record<string, unknown> = {};
      for (const key of Object.keys(row)) {
        result[key === oldKey ? newKey : key] = row[key];
      }
      return result;
    });
    onChange(newData, categoryKey, newSeriesKeys);
  };

  const handleRemoveSeries = (seriesKey: string) => {
    if (seriesKeys.length <= 1) return;

    const newSeriesKeys = seriesKeys.filter((k) => k !== seriesKey);
    const newData = data.map((row) => {
      const { [seriesKey]: _removed, ...rest } = row;
      return rest;
    });
    onChange(newData, categoryKey, newSeriesKeys);
  };

  const getCellError = (
    rowIndex: number,
    columnKey: string
  ): ValidationError | undefined => {
    return validationErrors.find(
      (e) => e.row_index === rowIndex && e.column === columnKey
    );
  };

  // Build columns dynamically: category + series columns + actions
  const columns: Column<RowWithIndex>[] = [];

  // Category column (frozen for horizontal scroll)
  columns.push({
    key: categoryKey,
    name: categoryKey,
    editable: true,
    renderEditCell: renderTextEditor,
    frozen: true,
    width: 200,
    renderCell: ({ row }: RenderCellProps<RowWithIndex>) => {
      const error = getCellError(row._index, categoryKey);
      return (
        <div
          className={`flex items-center gap-2 h-full px-2 ${error ? 'border-l-2 border-error' : ''}`}
        >
          {error && (
            <span title={error.message}>
              <AlertCircle
                className="w-4 h-4 text-error flex-shrink-0"
                aria-label={error.message}
              />
            </span>
          )}
          <span>{String(row[categoryKey] ?? '')}</span>
        </div>
      );
    },
  });

  // Series columns
  for (const seriesKey of seriesKeys) {
    columns.push({
      key: seriesKey,
      name: seriesKey,
      editable: true,
      width: 150,
      renderHeaderCell: () => (
        <SeriesHeaderEditor
          seriesKey={seriesKey}
          seriesKeys={seriesKeys}
          categoryKey={categoryKey}
          onRename={handleRenameSeries}
          onRemove={handleRemoveSeries}
          canRemove={seriesKeys.length > 1}
        />
      ),
      renderEditCell: ({ row, onRowChange, onClose }) => (
        <input
          autoFocus
          type="number"
          className="w-full h-full px-2 border-none outline-none"
          value={Number(row[seriesKey] ?? 0)}
          onChange={(e) =>
            onRowChange({ ...row, [seriesKey]: Number(e.target.value) })
          }
          onBlur={() => onClose(true)}
        />
      ),
      renderCell: ({ row }: RenderCellProps<RowWithIndex>) => {
        const error = getCellError(row._index, seriesKey);
        return (
          <div
            className={`flex items-center gap-2 h-full px-2 ${error ? 'border-l-2 border-error' : ''}`}
          >
            {error && (
              <span title={error.message}>
                <AlertCircle
                  className="w-4 h-4 text-error flex-shrink-0"
                  aria-label={error.message}
                />
              </span>
            )}
            <span>{String(row[seriesKey] ?? 0)}</span>
          </div>
        );
      },
    });
  }

  // Actions column
  columns.push({
    key: '_actions',
    name: '',
    width: 60,
    renderCell: ({ row }: RenderCellProps<RowWithIndex>) => (
      <button
        onClick={() => handleDeleteRow(row._index)}
        className="p-1 rounded-sm cursor-pointer transition-colors text-surface-400 hover:text-error hover:bg-error-light"
        title="Delete row"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    ),
  });

  return (
    <div className="chart-data-editor space-y-4">
      <div className="overflow-x-auto rounded-sm border border-surface-200">
        <DataGrid
          columns={columns}
          rows={rowsWithIndex}
          onRowsChange={handleRowsChange}
          rowKeyGetter={(row) => row._index}
          enableVirtualization={false}
          className="rdg-light text-[13px]"
          style={{ ...gridStyleOverrides, height: Math.max(150, (data.length + 1) * 35 + 35) }}
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleAddRow}
          className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-sm cursor-pointer transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Row
        </button>

        <button
          onClick={handleAddSeries}
          className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-sm cursor-pointer transition-colors"
          title="Add series"
        >
          <Plus className="w-4 h-4" />
          Add Series
        </button>
      </div>

      {validationErrors.length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-error-light border border-error/20 rounded-sm text-sm text-error">
          <AlertCircle className="w-4 h-4" />
          {validationErrors.length} validation error(s) found
        </div>
      )}
    </div>
  );
}
